import ApplicationController from './application_controller';

// Smart paste button — when an editable element (input, textarea, or a
// CKEditor instance) has focus anywhere on the page, clicking this button
// inserts the referenced text into that editable at the cursor position.
// When no editable is focused, falls back to writing the text to the
// system clipboard and announces it (smart-paste:copied), which the button's
// toast controller turns into the shared "Copied to clipboard" toast.
//
// Implementation notes:
//   - Tracks the currently focused editable via document-level focusin.
//     Clears the tracked target when focus moves to a non-editable element
//     (unless that element is a smart-paste button — we leave the target
//     alone so the click handler can find it).
//   - CKEditor focus is read from its own API
//     (editor.editing.view.document.isFocused) plus the globally-exposed
//     window.lastActiveCKEditor (set in shared/form/controls/_body.html.erb).
//   - On mousedown (which fires BEFORE focus shifts to the button), captures
//     the target + native cursor position so the click handler can insert
//     back at that exact spot even after the button steals focus.
//   - CKEditor insertion uses editor.model.insertContent, which respects
//     the editor's preserved selection (same pattern used in
//     quick_phrases_controller.js smartInsertForCKEditor).
//
// Values:
//   for       - id of the element whose textContent should be pasted/copied
//   prefix    - string prepended to the text (e.g. "LTG 30.0.0 ")
export default class extends ApplicationController {
  static values = { for: String, prefix: String };

  static ACTIVE_CLASS      = 'smart-paste--has-target';
  static HIGHLIGHT_CLASS   = 'smart-paste-target-active';
  static EDITABLE_SELECTOR = 'input:not([type=hidden]):not([type=submit]):not([type=button]):not([type=reset]):not([type=radio]):not([type=checkbox]):not([type=file]), textarea, [contenteditable="true"]';

  connect() {
    this._onFocusIn  = this.#handleFocusIn.bind(this);
    this._onFocusOut = this.#handleFocusOut.bind(this);
    this._onMouseDn  = this.#captureTarget.bind(this);
    document.addEventListener('focusin', this._onFocusIn);
    document.addEventListener('focusout', this._onFocusOut);
    this.element.addEventListener('mousedown', this._onMouseDn);
    this.#syncUI();
  }

  disconnect() {
    document.removeEventListener('focusin', this._onFocusIn);
    document.removeEventListener('focusout', this._onFocusOut);
    this.element.removeEventListener('mousedown', this._onMouseDn);
    this.#clearHighlight();
  }

  async perform(event) {
    event?.preventDefault();
    const text = this.#fullText();
    if (!text) return;

    const ckEditor = this._capturedCkEditor;
    const native   = this._capturedNative;

    if (ckEditor) {
      this.#insertIntoCkEditor(ckEditor, text);
    } else if (native) {
      this.#insertIntoNative(native, text, this._capturedSelection);
    } else {
      // Nothing on the page shows a copy landing, so the button says so: the shared
      // "Copied to clipboard" toast, through the toast controller the button also carries
      // (data-action="smart-paste:copied->toast#show").
      try {
        await navigator.clipboard.writeText(text);
        this.dispatch('copied');
      } catch (_) { /* silent */ }
    }

    this._capturedCkEditor = null;
    this._capturedNative   = null;
    this._capturedSelection = null;
  }

  // --- focus tracking ---

  #handleFocusIn(event) {
    const el = event.target;
    if (!el) return;

    if (this.#isNativeEditable(el)) {
      this._currentNativeTarget = el;
    } else if (el.closest && el.closest('[data-controller~="smart-paste"]')) {
      // Focused a smart-paste button — keep the remembered target so the
      // click handler can still insert into it.
    } else {
      this._currentNativeTarget = null;
    }
    this.#syncUI();
  }

  #handleFocusOut(event) {
    // Fires when focus leaves an element. If nothing new gets focus
    // (e.g. user clicks empty page area), focusin never fires, so our
    // sync has to run from here. Defer to the next tick so any pending
    // focusin (moving to a new target) runs first and the sync picks up
    // the newest state.
    setTimeout(() => {
      const ae = document.activeElement;
      const stillEditable = ae && this.#isNativeEditable(ae);
      const onSmartPaste  = ae && ae.closest && ae.closest('[data-controller~="smart-paste"]');
      if (!stillEditable && !onSmartPaste) {
        this._currentNativeTarget = null;
      }
      this.#syncUI();
    }, 0);
  }

  #isNativeEditable(el) {
    return !!(el.matches && el.matches(this.constructor.EDITABLE_SELECTOR));
  }

  #ckEditorIsFocused() {
    const ed = window.lastActiveCKEditor;
    return !!(ed && ed.editing && ed.editing.view && ed.editing.view.document && ed.editing.view.document.isFocused);
  }

  #syncUI() {
    const hasTarget = !!this._currentNativeTarget || this.#ckEditorIsFocused();
    this.element.classList.toggle(this.constructor.ACTIVE_CLASS, hasTarget);
    this.#applyHighlight();
  }

  #applyHighlight() {
    const target = this._currentNativeTarget;
    if (target === this._highlightedEl) return;
    this.#clearHighlight();
    if (target) {
      target.classList.add(this.constructor.HIGHLIGHT_CLASS);
      this._highlightedEl = target;
    }
  }

  #clearHighlight() {
    if (this._highlightedEl) {
      this._highlightedEl.classList.remove(this.constructor.HIGHLIGHT_CLASS);
      this._highlightedEl = null;
    }
  }

  // --- capture target BEFORE click steals focus ---

  #captureTarget() {
    // mousedown fires before focus shifts. Current native target + its
    // cursor range must be captured now, otherwise target.focus() in the
    // click handler will reset the cursor.
    this._capturedCkEditor = this.#ckEditorIsFocused() ? window.lastActiveCKEditor : null;
    this._capturedNative   = this._currentNativeTarget || null;
    this._capturedSelection = null;
    if (this._capturedNative && typeof this._capturedNative.selectionStart === 'number') {
      this._capturedSelection = {
        start: this._capturedNative.selectionStart,
        end:   this._capturedNative.selectionEnd
      };
    }
  }

  // --- insertion ---

  #fullText() {
    const src = document.getElementById(this.forValue);
    const body = src?.textContent?.trim();
    if (!body) return '';
    return `${this.prefixValue || ''}${body}`;
  }

  #insertIntoCkEditor(editor, text) {
    editor.editing.view.focus();
    editor.model.change((writer) => {
      const insertable = writer.createText(text);
      editor.model.insertContent(insertable);
    });
  }

  #insertIntoNative(target, text, range) {
    target.focus();
    if (range && typeof target.setRangeText === 'function') {
      target.setRangeText(text, range.start, range.end, 'end');
    } else if (typeof target.setRangeText === 'function') {
      const start = target.selectionStart ?? target.value.length;
      const end   = target.selectionEnd   ?? start;
      target.setRangeText(text, start, end, 'end');
    } else {
      target.value = (target.value || '') + text;
    }
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
